import type { RoofEdge } from "building-types/roofs/roofEdgeTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import type { WallPlaneMasses, WallPlaneSubdomainMiterMass } from "building-types/walls2/wallPlane";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { faceCopy, faceMove, faceNew, faceReverse } from "geom/brep";
import {
  addOutsidePolygonsToBSP,
  buildBSPNode,
  buildEdgePolygon,
  buildPolygonsFromFaces,
  polygonBSPMoveAlongNormal,
  polygonBSPNew,
  splitPolygonsBSP,
} from "geom/bsp";
import { splitFaceBSP, splitFacesBSP } from "geom/bspClipping";
import type { MaxBSPDepthErrorHandler, NodeBSP, PolygonBSP } from "geom/bspTypes";
import { ZERO_EPSILON } from "geom/core";
import { intersectPlanePlane, intersectRay2Ray2 } from "geom/intersect";
import { CurveCurvePointIntersection, intersectLine2Line2 } from "geom/intersectLine2Line2";
import {
  planeCanonicalFromSegment,
  planeHash,
  planeNewXY,
  planeProject,
  planeProject3,
  planeIsCoplanar,
} from "geom/plane";
import { planeBSPByThreePoints } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import { polyLine2New } from "geom/polyLine2Types";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2Cross, vec2DistSquared, vec2IsBetween } from "geom/vec2";

const MULTIGABLE_BSP_EPSILON_SQUARED = MULTIGABLE_BSP_EPSILON * MULTIGABLE_BSP_EPSILON;

const LEVEL_XY_PLANE = planeNewXY();

export interface UpdateInsideRoofBSPWithWallPlaneMassesOptions {
  readonly rootBSP: NodeBSP | null;
  readonly subRoofId: string;
  readonly exactEdges: RoofEdge[];
  readonly roofTopFacesWithOverlap: RoofFace[];
  readonly bottomOffset?: number;
  readonly topOffset?: number;
  readonly addBottom?: boolean;
  readonly extraHipFaces?: RoofFace[];
  readonly maxDepthErrorHandle: MaxBSPDepthErrorHandler;
  readonly wallPlaneMasses: WallPlaneMasses;
  readonly peakHeight: number;
}

export interface BuildWallClipPolygonsOptions {
  readonly exactEdges: RoofEdge[];
  readonly bottomOffset: number;
  readonly addBottom: boolean;
  readonly wallPlaneMasses: WallPlaneMasses;
  readonly peakHeight: number;
}

/**
 * Builds the wall-clip polygons (front faces, gap fills, step faces,
 * corner miters) without performing BSP operations. Useful for testing
 * the polygon-generation logic in isolation.
 */
export function buildWallClipPolygons({
  exactEdges,
  bottomOffset,
  addBottom,
  wallPlaneMasses,
  peakHeight,
}: BuildWallClipPolygonsOptions): { polygons: PolygonBSP[]; edges: ResolvedEdgeGrid[] } {
  const clipTopPolygons: PolygonBSP[] = [];
  const edgeProfiles: { start: CornerOffsetProfile; end: CornerOffsetProfile }[] = [];

  const resolvedEdgeGrids: ResolvedEdgeGrid[] = getWallPlaneGridsFromRoofEdges(
    exactEdges,
    wallPlaneMasses,
    bottomOffset,
    peakHeight,
  );

  for (const edgeGrid of resolvedEdgeGrids) {
    const gridSlice = edgeGrid.gridSlice;
    const { startx: edgeStartx, starty: edgeStarty, endx: edgeEndx, endy: edgeEndy } = edgeGrid;

    // Trackers of the wall offset profile at the start and end of each edge
    // These will be used to close off corners in a second pass
    const startProfile: CornerOffsetProfile = [];
    const endProfile: CornerOffsetProfile = [];

    if (!gridSlice) {
      // No matching wall-plane grid — use the exact edge with a small outward
      // epsilon so coplanar exterior finish is not classified outside.
      const polygon = makeFrontFacePolygon(
        edgeStartx,
        edgeStarty,
        edgeEndx,
        edgeEndy,
        bottomOffset,
        peakHeight,
        0,
        startProfile,
        endProfile,
      );
      if (polygon) {
        clipTopPolygons.push(polygon);
      }
      edgeProfiles.push({ start: startProfile, end: endProfile });
      continue;
    }

    const { grid, startRow, endRow, startCol, endCol, minV, maxV } = gridSlice;

    // Variables local to this loop are stored in edgeInfo to reduce clutter when passing between helper functions
    const edgeInfo: EdgeInfo = {
      // does the start->end of the edge go in the same or opposite direction of the wall core grid?
      sameDirection: undefined,

      // accumulators that track chunks of undefined cells
      lastFilledUx: undefined,
      lastFilledUy: undefined,
      lastFilledV: minV,

      // the bottom and top of the current row
      rowBottom: undefined,
      rowTop: undefined,

      // the coordinates of the current cell's wall core edge (in 2d, z=rowBottom implied)
      ax: 0,
      ay: 0,
      bx: 0,
      by: 0,

      // the outward cladding offset of the current cell's wall core edge
      claddingOffset: 0,
      plane: gridSlice.plane,
    };

    for (let row = startRow; row <= endRow; row++) {
      const rowCells = grid[row];
      if (!rowCells) {
        continue;
      }

      edgeInfo.sameDirection = undefined;
      edgeInfo.lastFilledUx = undefined;
      edgeInfo.lastFilledUy = undefined;
      edgeInfo.rowBottom = undefined;
      edgeInfo.rowTop = undefined;

      for (let col = startCol; col <= endCol; col++) {
        const cell = rowCells[col];
        if (!cell) {
          continue;
        }

        const frontFacePolygon = orientMiterEdgeAndBuildFace(
          edgeInfo,
          cell,
          edgeStartx,
          edgeStarty,
          edgeEndx,
          edgeEndy,
          minV,
          maxV,
          startProfile,
          endProfile,
        );

        if (frontFacePolygon) {
          clipTopPolygons.push(frontFacePolygon);
        }

        const { ax, ay, bx, by, rowBottom, rowTop, sameDirection, lastFilledUx, lastFilledUy } = edgeInfo;
        if (
          rowBottom === undefined ||
          rowTop === undefined ||
          sameDirection === undefined ||
          lastFilledUx === undefined ||
          lastFilledUy === undefined
        ) {
          continue;
        }

        // Gap before this cell (e.g. an opening) — no wall cladding here, only epsilon.
        const { startx, starty, endx, endy, newLastFilledx, newLastFilledy } = getGapEdge(
          lastFilledUx,
          lastFilledUy,
          ax,
          ay,
          bx,
          by,
          sameDirection,
          edgeInfo.plane,
        );

        if (startx !== undefined && starty !== undefined && endx !== undefined && endy !== undefined) {
          const gapAtStart =
            vec2DistSquaredInVerticalPlane(edgeInfo.plane, startx, starty, edgeStartx, edgeStarty) <
            MULTIGABLE_BSP_EPSILON_SQUARED;
          const gapAtEnd =
            vec2DistSquaredInVerticalPlane(edgeInfo.plane, endx, endy, edgeEndx, edgeEndy) <
            MULTIGABLE_BSP_EPSILON_SQUARED;
          const polygon = makeFrontFacePolygon(
            startx,
            starty,
            endx,
            endy,
            rowBottom,
            rowTop,
            0,
            gapAtStart ? startProfile : undefined,
            gapAtEnd ? endProfile : undefined,
          );
          if (polygon) {
            clipTopPolygons.push(polygon);
          }
        }

        edgeInfo.lastFilledUx = newLastFilledx;
        edgeInfo.lastFilledUy = newLastFilledy;

        addNeighborStepFaces(
          edgeInfo,
          grid,
          row,
          col,
          edgeStartx,
          edgeStarty,
          edgeEndx,
          edgeEndy,
          addBottom,
          minV,
          clipTopPolygons,
        );
      }
      const { rowBottom, rowTop, sameDirection, lastFilledUx, lastFilledUy } = edgeInfo;
      if (rowBottom === undefined || rowTop === undefined || sameDirection === undefined) {
        continue;
      }

      // Gap before the end of the row — no wall cladding here, only epsilon.
      if (
        lastFilledUx !== undefined &&
        lastFilledUy !== undefined &&
        vec2IsBetweenInVerticalPlane(
          edgeStartx,
          edgeStarty,
          edgeEndx,
          edgeEndy,
          lastFilledUx,
          lastFilledUy,
          edgeInfo.plane,
        )
      ) {
        let startx: number;
        let starty: number;
        let endx: number;
        let endy: number;
        let trailAtStart: boolean;
        let trailAtEnd: boolean;
        if (sameDirection) {
          startx = lastFilledUx;
          starty = lastFilledUy;
          endx = edgeEndx;
          endy = edgeEndy;
          trailAtStart =
            vec2DistSquaredInVerticalPlane(edgeInfo.plane, startx, starty, edgeStartx, edgeStarty) <
            MULTIGABLE_BSP_EPSILON_SQUARED;
          trailAtEnd = true;
        } else {
          startx = edgeStartx;
          starty = edgeStarty;
          endx = lastFilledUx;
          endy = lastFilledUy;
          trailAtStart = true;
          trailAtEnd =
            vec2DistSquaredInVerticalPlane(edgeInfo.plane, endx, endy, edgeEndx, edgeEndy) <
            MULTIGABLE_BSP_EPSILON_SQUARED;
        }
        const polygon = makeFrontFacePolygon(
          startx,
          starty,
          endx,
          endy,
          rowBottom,
          rowTop,
          0,
          trailAtStart ? startProfile : undefined,
          trailAtEnd ? endProfile : undefined,
        );
        if (polygon) {
          clipTopPolygons.push(polygon);
        }
      }

      // Vertical gap between datum rows — no wall cladding here, only epsilon.
      if (edgeInfo.lastFilledV < rowBottom) {
        const polygon = makeFrontFacePolygon(
          edgeStartx,
          edgeStarty,
          edgeEndx,
          edgeEndy,
          edgeInfo.lastFilledV,
          rowBottom,
          0,
          startProfile,
          endProfile,
        );
        if (polygon) {
          clipTopPolygons.push(polygon);
        }
      }

      edgeInfo.lastFilledV = rowTop;
    }

    // Gap above the last filled row — no wall cladding here, only epsilon.
    if (edgeInfo.lastFilledV < maxV) {
      const polygon = makeFrontFacePolygon(
        edgeStartx,
        edgeStarty,
        edgeEndx,
        edgeEndy,
        edgeInfo.lastFilledV,
        maxV,
        0,
        startProfile,
        endProfile,
      );
      if (polygon) {
        clipTopPolygons.push(polygon);
      }
    }

    edgeProfiles.push({ start: startProfile, end: endProfile });
  }

  // Corner miter pass: process convex corners between consecutive edges
  for (let i = 0; i < resolvedEdgeGrids.length; i++) {
    const edgeA = resolvedEdgeGrids[i];
    const edgeB = resolvedEdgeGrids[(i + 1) % resolvedEdgeGrids.length];

    const { startx: asx, starty: asy, endx: aex, endy: aey } = edgeA;
    const { startx: bsx, starty: bsy, endx: bex, endy: bey } = edgeB;

    // Verify the edges actually share a corner point
    if (vec2DistSquared(aex, aey, bsx, bsy) >= MULTIGABLE_BSP_EPSILON_SQUARED) {
      continue;
    }

    const dirAx = aex - asx;
    const dirAy = aey - asy;
    const dirBx = bex - bsx;
    const dirBy = bey - bsy;

    const cross = vec2Cross(dirAx, dirAy, dirBx, dirBy);

    // Skip parallel edges (cross ≈ 0) and concave corners (cross < 0).
    // Positive cross means convex for a CCW-wound perimeter.
    if (cross <= MULTIGABLE_BSP_EPSILON) {
      continue;
    }

    const profileA = edgeProfiles[i].end;
    const profileB = edgeProfiles[(i + 1) % resolvedEdgeGrids.length].start;

    const merged = mergeOffsetProfiles(profileA, profileB);

    addCornerMiterFaces(clipTopPolygons, aex, aey, dirAx, dirAy, dirBx, dirBy, merged, addBottom);
  }

  return { polygons: clipTopPolygons, edges: resolvedEdgeGrids };
}

export function updateInsideRoofBSPWithWallPlaneMasses({
  rootBSP,
  subRoofId,
  roofTopFacesWithOverlap,
  exactEdges,
  bottomOffset = 0.0,
  topOffset = 0.0,
  addBottom = false,
  extraHipFaces = [],
  maxDepthErrorHandle,
  wallPlaneMasses,
  peakHeight,
}: UpdateInsideRoofBSPWithWallPlaneMassesOptions): NodeBSP | null {
  const { polygons: clipTopPolygons, edges: resolvedEdges } = buildWallClipPolygons({
    exactEdges,
    bottomOffset,
    addBottom,
    wallPlaneMasses,
    peakHeight,
  });

  // Top/bottom face processing and BSP merge
  // The handling of top faces is very similar to how they're handled in updateInsideRoofBSP, in the non-wall-planes pipeline
  const polygons: PolygonBSP[] = [];

  const extraHipPolygons = buildPolygonsFromFaces({
    faces: extraHipFaces.map((x) => x.face),
  });

  const extraHipFacesBSP = buildBSPNode({
    polygons: extraHipPolygons,
    epsilon: MULTIGABLE_BSP_EPSILON,
    maxDepthErrorHandle,
  });

  const clipTopBSP = buildBSPNode({
    polygons: clipTopPolygons,
    maxDepthErrorHandle,
  });

  // Build a BSP from the roof top faces to clip the wall faces against
  const allRoofFaces = [...roofTopFacesWithOverlap.map((rf) => rf.face), ...extraHipFaces.map((rf) => rf.face)];
  const roofFacePolygons = buildPolygonsFromFaces({ faces: allRoofFaces, offsetZ: topOffset });
  const roofFaceBSP = buildBSPNode({
    polygons: roofFacePolygons,
    epsilon: MULTIGABLE_BSP_EPSILON,
    maxDepthErrorHandle,
  });

  // Clip wall faces (front faces, step faces, corner miters) by the roof
  // surfaces, keeping only the portions below the roof
  if (roofFaceBSP) {
    splitPolygonsBSP({
      node: roofFaceBSP,
      polygons: clipTopPolygons,
      coplanarsToFront: true,
      backPolygons: polygons,
      epsilon: MULTIGABLE_BSP_EPSILON,
    });
  } else {
    polygons.push(...clipTopPolygons);
  }

  const addTopFace = (topFace: Face, isExtraHipFace: boolean = false) => {
    if (!topFace) {
      return;
    }

    const topFacesNoOverhang: Face[] = [];
    const offsetTopFace = faceMove(faceCopy(topFace), 0, 0, topOffset);

    if (offsetTopFace) {
      splitFaceBSP(offsetTopFace, {
        node: clipTopBSP,
        coplanarsToFront: true,
        backFaces: topFacesNoOverhang,
        epsilon: MULTIGABLE_BSP_EPSILON,
      });
    }

    const facesToAdd: Face[] = [];

    if (extraHipFacesBSP && !isExtraHipFace) {
      splitFacesBSP(topFacesNoOverhang, {
        node: extraHipFacesBSP,
        coplanarsToFront: false,
        backFaces: facesToAdd,
        epsilon: MULTIGABLE_BSP_EPSILON,
      });
    } else {
      facesToAdd.push(...topFacesNoOverhang);
    }

    polygons.push(...buildPolygonsFromFaces({ faces: facesToAdd }));
  };

  for (const topFace of roofTopFacesWithOverlap) {
    addTopFace(topFace.face);
  }

  for (const roofFace of extraHipFaces) {
    addTopFace(roofFace.face, true);
  }

  if (addBottom) {
    const bottomPl = polyLine2New(resolvedEdges.length * 2 + 2);
    for (let i = 0; i < resolvedEdges.length; i++) {
      const { startx, starty } = resolvedEdges[i];
      bottomPl[i * 2] = startx;
      bottomPl[i * 2 + 1] = starty;
    }
    bottomPl[resolvedEdges.length * 2] = resolvedEdges[0].startx;
    bottomPl[resolvedEdges.length * 2 + 1] = resolvedEdges[0].starty;

    const bottomPlane = planeNewXY(0, 0, bottomOffset);
    const bottomFace = faceReverse(faceNew([bottomPl], bottomPlane));
    polygons.push(...buildPolygonsFromFaces({ faces: [bottomFace] }));
  }

  return addOutsidePolygonsToBSP({
    node: rootBSP,
    polygons,
    coplanarsToFront: true,
    keepOutsideCoplanars: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
    userData: subRoofId,
  });
}

interface EdgeInfo {
  sameDirection: boolean | undefined;
  lastFilledUx: number | undefined;
  lastFilledUy: number | undefined;
  lastFilledV: number;
  rowBottom: number | undefined;
  rowTop: number | undefined;
  ax: number;
  ay: number;
  bx: number;
  by: number;
  claddingOffset: number;
  plane: Plane;
}

/**
 * Outward cladding thickness for a wall-plane cell.
 * Prefers building-graph outward side; falls back to sameDirection left/right
 * when face-node outward side cannot be resolved.
 */
function outwardCladdingOffsetForCell(cell: WallPlaneSubdomainMiterMass, sameDirectionFallback: boolean): number {
  const { leftCladdingOffset, rightCladdingOffset } = cell.miterEdge;
  return sameDirectionFallback ? rightCladdingOffset : leftCladdingOffset;
}

function vec2IsBetweenInVerticalPlane(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  xtest: number,
  ytest: number,
  plane: Plane,
) {
  const [x0Plane, y0Plane] = planeProject(plane, x0, y0, 0);
  const [x1Plane, y1Plane] = planeProject(plane, x1, y1, 0);
  const [xtestPlane, ytestPlane] = planeProject(plane, xtest, ytest, 0);

  return vec2IsBetween(x0Plane, y0Plane, x1Plane, y1Plane, xtestPlane, ytestPlane);
}

function vec2DistSquaredInVerticalPlane(plane: Plane, x0: number, y0: number, x1: number, y1: number) {
  const [x0Plane, y0Plane] = planeProject(plane, x0, y0, 0);
  const [x1Plane, y1Plane] = planeProject(plane, x1, y1, 0);

  return vec2DistSquared(x0Plane, y0Plane, x1Plane, y1Plane);
}

/**
 * Orients a cell's miter edge to match the perimeter direction and builds
 * the front-face polygon for roof clipping. Mutates `edgeInfo` with the
 * oriented endpoints, cladding offset, and (on first cell) direction and
 * tracking state. Returns the front-face polygon or null.
 */
function orientMiterEdgeAndBuildFace(
  edgeInfo: EdgeInfo,
  cell: WallPlaneSubdomainMiterMass,
  edgeStartx: number,
  edgeStarty: number,
  edgeEndx: number,
  edgeEndy: number,
  minV: number,
  maxV: number,
  startProfile: CornerOffsetProfile,
  endProfile: CornerOffsetProfile,
): PolygonBSP | null {
  const { miterEdge } = cell;
  const [msx, msy, mex, mey] = miterEdge.edge;

  if (edgeInfo.rowBottom === undefined || edgeInfo.rowTop === undefined) {
    edgeInfo.rowBottom = Math.max(cell.bottom, minV);
    edgeInfo.rowTop = Math.min(cell.top, maxV);
  }

  // If the miter edge opposes the perimeter direction, swap to
  // keep the polygon normal consistent with the roof exterior.
  if (
    edgeInfo.sameDirection === undefined ||
    edgeInfo.lastFilledUx === undefined ||
    edgeInfo.lastFilledUy === undefined
  ) {
    const perimDx = edgeEndx - edgeStartx;
    const perimDy = edgeEndy - edgeStarty;
    const miterDx = mex - msx;
    const miterDy = mey - msy;
    const dot = perimDx * miterDx + perimDy * miterDy;
    edgeInfo.sameDirection = dot >= 0;

    if (edgeInfo.sameDirection) {
      edgeInfo.lastFilledUx = edgeStartx;
      edgeInfo.lastFilledUy = edgeStarty;
    } else {
      edgeInfo.lastFilledUx = edgeEndx;
      edgeInfo.lastFilledUy = edgeEndy;
    }
  }

  const { sameDirection } = edgeInfo;
  edgeInfo.ax = sameDirection ? msx : mex;
  edgeInfo.ay = sameDirection ? msy : mey;
  edgeInfo.bx = sameDirection ? mex : msx;
  edgeInfo.by = sameDirection ? mey : msy;
  if (
    vec2IsBetweenInVerticalPlane(edgeInfo.ax, edgeInfo.ay, edgeEndx, edgeEndy, edgeStartx, edgeStarty, edgeInfo.plane)
  ) {
    edgeInfo.ax = edgeStartx;
    edgeInfo.ay = edgeStarty;
  }
  if (
    vec2IsBetweenInVerticalPlane(edgeStartx, edgeStarty, edgeInfo.bx, edgeInfo.by, edgeEndx, edgeEndy, edgeInfo.plane)
  ) {
    edgeInfo.bx = edgeEndx;
    edgeInfo.by = edgeEndy;
  }

  edgeInfo.claddingOffset = outwardCladdingOffsetForCell(cell, sameDirection);
  const atStart =
    vec2DistSquaredInVerticalPlane(edgeInfo.plane, edgeInfo.ax, edgeInfo.ay, edgeStartx, edgeStarty) <
    MULTIGABLE_BSP_EPSILON_SQUARED;
  const atEnd =
    vec2DistSquaredInVerticalPlane(edgeInfo.plane, edgeInfo.bx, edgeInfo.by, edgeEndx, edgeEndy) <
    MULTIGABLE_BSP_EPSILON_SQUARED;
  return makeFrontFacePolygon(
    edgeInfo.ax,
    edgeInfo.ay,
    edgeInfo.bx,
    edgeInfo.by,
    edgeInfo.rowBottom,
    edgeInfo.rowTop,
    edgeInfo.claddingOffset,
    atStart ? startProfile : undefined,
    atEnd ? endProfile : undefined,
  );
}

/**
 * Adds step-face polygons where the current cell's cladding offset exceeds
 * its neighbor's offset, creating the vertical and horizontal "steps" at
 * adjacency boundaries.
 */
function addNeighborStepFaces(
  edgeInfo: EdgeInfo,
  grid: (WallPlaneSubdomainMiterMass | undefined)[][],
  row: number,
  col: number,
  edgeStartx: number,
  edgeStarty: number,
  edgeEndx: number,
  edgeEndy: number,
  addBottom: boolean,
  minV: number,
  clipTopPolygons: PolygonBSP[],
): void {
  const { ax, ay, bx, by, claddingOffset, sameDirection, rowBottom, rowTop } = edgeInfo;
  if (rowBottom === undefined || rowTop === undefined || sameDirection === undefined) {
    return;
  }

  const edgeDx = bx - ax;
  const edgeDy = by - ay;
  const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);
  if (edgeLen <= MULTIGABLE_BSP_EPSILON) {
    return;
  }

  const nx = edgeDy / edgeLen;
  const ny = -edgeDx / edgeLen;

  const rowCells = grid[row];

  const getVerticalNeighborOffset = (direction: 1 | -1, touchZ: number): number => {
    for (let r = row + direction; r >= 0 && r < grid.length; r += direction) {
      const neighborRow = grid[r];
      if (!neighborRow) {
        continue;
      }

      const cell = neighborRow[col];

      if (cell === undefined) {
        continue;
      }

      if (
        (direction > 0 && Math.abs(touchZ - cell.bottom) < MULTIGABLE_BSP_EPSILON) ||
        (direction < 0 && Math.abs(touchZ - cell.top) < MULTIGABLE_BSP_EPSILON)
      ) {
        return outwardCladdingOffsetForCell(cell, sameDirection);
      }
      return MULTIGABLE_BSP_EPSILON;
    }
    return MULTIGABLE_BSP_EPSILON;
  };

  const getHorizontalNeighborOffset = (direction: 1 | -1, touchX: number, touchY: number): number => {
    for (let c = col + direction; c >= 0 && c < rowCells.length; c += direction) {
      const neighbor = rowCells[c];
      if (!neighbor) {
        continue;
      }
      const [neighborStartx, neighborStarty, neighborEndx, neighborEndy] = neighbor.miterEdge.edge;
      let neighborEdgeComparex, neighborEdgeComparey;
      if (direction > 0) {
        neighborEdgeComparex = neighborStartx;
        neighborEdgeComparey = neighborStarty;
      } else {
        neighborEdgeComparex = neighborEndx;
        neighborEdgeComparey = neighborEndy;
      }
      if (
        vec2DistSquaredInVerticalPlane(edgeInfo.plane, touchX, touchY, neighborEdgeComparex, neighborEdgeComparey) <
        MULTIGABLE_BSP_EPSILON_SQUARED
      ) {
        return outwardCladdingOffsetForCell(neighbor, sameDirection);
      }
      return MULTIGABLE_BSP_EPSILON;
    }
    return MULTIGABLE_BSP_EPSILON;
  };

  if (grid[row][col] === undefined) {
    return;
  }
  const [msx, msy, mex, mey] = grid[row][col].miterEdge.edge;
  const prevOffset = getHorizontalNeighborOffset(-1, msx, msy);
  const nextOffset = getHorizontalNeighborOffset(1, mex, mey);

  const rightNeighborOffset = sameDirection ? nextOffset : prevOffset;
  const leftNeighborOffset = sameDirection ? prevOffset : nextOffset;

  const rightPx = bx;
  const rightPy = by;
  const rightAtCorner =
    vec2DistSquaredInVerticalPlane(edgeInfo.plane, rightPx, rightPy, edgeStartx, edgeStarty) <
      MULTIGABLE_BSP_EPSILON_SQUARED ||
    vec2DistSquaredInVerticalPlane(edgeInfo.plane, rightPx, rightPy, edgeEndx, edgeEndy) <
      MULTIGABLE_BSP_EPSILON_SQUARED;
  if (claddingOffset > rightNeighborOffset && !rightAtCorner) {
    addVerticalStepFace(
      clipTopPolygons,
      rightPx,
      rightPy,
      rowBottom,
      rowTop,
      nx,
      ny,
      rightNeighborOffset,
      claddingOffset,
      false,
    );
  }

  const leftPx = ax;
  const leftPy = ay;
  const leftAtCorner =
    vec2DistSquaredInVerticalPlane(edgeInfo.plane, leftPx, leftPy, edgeStartx, edgeStarty) <
      MULTIGABLE_BSP_EPSILON_SQUARED ||
    vec2DistSquaredInVerticalPlane(edgeInfo.plane, leftPx, leftPy, edgeEndx, edgeEndy) < MULTIGABLE_BSP_EPSILON_SQUARED;
  if (claddingOffset > leftNeighborOffset && !leftAtCorner) {
    addVerticalStepFace(
      clipTopPolygons,
      leftPx,
      leftPy,
      rowBottom,
      rowTop,
      nx,
      ny,
      leftNeighborOffset,
      claddingOffset,
      true,
    );
  }

  const aboveNeighborOffset = getVerticalNeighborOffset(1, rowTop);
  if (claddingOffset > aboveNeighborOffset) {
    addHorizontalStepFace(clipTopPolygons, ax, ay, bx, by, rowTop, nx, ny, aboveNeighborOffset, claddingOffset, true);
  }

  const belowNeighborOffset = getVerticalNeighborOffset(-1, rowBottom);
  if (claddingOffset > belowNeighborOffset && !(!addBottom && rowBottom === minV)) {
    addHorizontalStepFace(
      clipTopPolygons,
      ax,
      ay,
      bx,
      by,
      rowBottom,
      nx,
      ny,
      belowNeighborOffset,
      claddingOffset,
      false,
    );
  }
}

/**
 * Computes the gap-fill segment between the last filled position and the
 * current cell along the edge. Returns the gap endpoints (if any) and
 * the updated last-filled position.
 */
function getGapEdge(
  lastFilledUx: number,
  lastFilledUy: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  sameDirection: boolean,
  plane: Plane,
): {
  startx: number | undefined;
  starty: number | undefined;
  endx: number | undefined;
  endy: number | undefined;
  newLastFilledx: number;
  newLastFilledy: number;
} {
  let startx: number | undefined;
  let starty: number | undefined;
  let endx: number | undefined;
  let endy: number | undefined;
  let newLastFilledx: number;
  let newLastFilledy: number;
  if (sameDirection) {
    if (vec2IsBetweenInVerticalPlane(lastFilledUx, lastFilledUy, bx, by, ax, ay, plane)) {
      startx = lastFilledUx;
      starty = lastFilledUy;
      endx = ax;
      endy = ay;
    }
    newLastFilledx = bx;
    newLastFilledy = by;
  } else {
    if (vec2IsBetweenInVerticalPlane(lastFilledUx, lastFilledUy, ax, ay, bx, by, plane)) {
      startx = bx;
      starty = by;
      endx = lastFilledUx;
      endy = lastFilledUy;
    }
    newLastFilledx = ax;
    newLastFilledy = ay;
  }

  return { startx, starty, endx, endy, newLastFilledx, newLastFilledy };
}

/**
 * Adds a vertical step-face quad at a single boundary point between two
 * horizontally adjacent cells (left/right neighbors). The quad spans
 * zBottom→zTop vertically and innerOffset→outerOffset along the front
 * face normal, bridging the depth difference.
 */
function addVerticalStepFace(
  polygons: PolygonBSP[],
  px: number,
  py: number,
  zBottom: number,
  zTop: number,
  nx: number,
  ny: number,
  innerOffset: number,
  outerOffset: number,
  flip: boolean,
): void {
  const x1 = px + nx * innerOffset;
  const y1 = py + ny * innerOffset;

  const x2 = px + nx * outerOffset;
  const y2 = py + ny * outerOffset;

  let verts: number[];
  if (flip) {
    verts = [x2, y2, zBottom, x2, y2, zTop, x1, y1, zTop, x1, y1, zBottom];
  } else {
    verts = [x1, y1, zBottom, x1, y1, zTop, x2, y2, zTop, x2, y2, zBottom];
  }

  const bspPlane = planeBSPByThreePoints(
    verts[0],
    verts[1],
    verts[2],
    verts[3],
    verts[4],
    verts[5],
    verts[6],
    verts[7],
    verts[8],
  );
  if (!bspPlane) {
    return;
  }

  const polygon = polygonBSPNew(0, verts, bspPlane);
  polygons.push(polygon);
}

/**
 * Adds a horizontal step-face quad along the shared edge between two
 * vertically adjacent cells (above/below neighbors). The quad spans
 * the full cell width (ax,ay)→(bx,by) at a single Z elevation, and
 * innerOffset→outerOffset along the front face normal.
 */
function addHorizontalStepFace(
  polygons: PolygonBSP[],
  ax: number,
  ay: number,
  bx: number,
  by: number,
  z: number,
  nx: number,
  ny: number,
  innerOffset: number,
  outerOffset: number,
  flip: boolean,
): void {
  const x1 = ax + nx * innerOffset;
  const y1 = ay + ny * innerOffset;

  const x2 = bx + nx * innerOffset;
  const y2 = by + ny * innerOffset;

  const x3 = bx + nx * outerOffset;
  const y3 = by + ny * outerOffset;

  const x4 = ax + nx * outerOffset;
  const y4 = ay + ny * outerOffset;

  let verts: number[];
  if (flip) {
    verts = [x4, y4, z, x3, y3, z, x2, y2, z, x1, y1, z];
  } else {
    verts = [x1, y1, z, x2, y2, z, x3, y3, z, x4, y4, z];
  }

  const bspPlane = planeBSPByThreePoints(
    verts[0],
    verts[1],
    verts[2],
    verts[3],
    verts[4],
    verts[5],
    verts[6],
    verts[7],
    verts[8],
  );
  if (!bspPlane) {
    return;
  }

  const polygon = polygonBSPNew(0, verts, bspPlane);
  polygons.push(polygon);
}

/**
 * Creates a front-face BSP polygon for a wall segment from (ax,ay) to
 * (bx,by), extruded vertically from cellBottom to cellTop and offset
 * along the polygon normal by 'profileOffset'. `profileOffset` is
 * optionally recorded for corner resolution.
 */
function makeFrontFacePolygon(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cellBottom: number,
  cellTop: number,
  profileOffset: number,
  startProfile?: CornerOffsetProfile,
  endProfile?: CornerOffsetProfile,
) {
  const cellHeight = cellTop - cellBottom;

  if (cellHeight < MULTIGABLE_BSP_EPSILON) {
    return null;
  }

  if (startProfile) {
    startProfile.push({ zBottom: cellBottom, zTop: cellTop, offset: profileOffset });
  }
  if (endProfile) {
    endProfile.push({ zBottom: cellBottom, zTop: cellTop, offset: profileOffset });
  }

  if (vec2DistSquared(ax, ay, bx, by) < MULTIGABLE_BSP_EPSILON_SQUARED) {
    return null;
  }

  let frontFacePolygon = buildEdgePolygon(ax, ay, cellBottom, bx, by, cellBottom, 0.0, 0.0, cellHeight);
  if (!frontFacePolygon) {
    return null;
  }

  frontFacePolygon = polygonBSPMoveAlongNormal(frontFacePolygon, profileOffset);

  return frontFacePolygon;
}

interface MergedCornerBands {
  readonly zBreaks: readonly number[];
  readonly offsetsA: readonly number[];
  readonly offsetsB: readonly number[];
}

/**
 * Merges two corner offset profiles into a unified set of Z bands.
 * Each output band spans [zBreaks[i], zBreaks[i+1]] and has the
 * offset from each side (A and B) that covers that range.
 */
function mergeOffsetProfiles(profileA: CornerOffsetProfile, profileB: CornerOffsetProfile): MergedCornerBands {
  const zSet = new Set<number>();
  for (const band of profileA) {
    zSet.add(band.zBottom);
    zSet.add(band.zTop);
  }
  for (const band of profileB) {
    zSet.add(band.zBottom);
    zSet.add(band.zTop);
  }

  const zBreaks = Array.from(zSet).sort((a, b) => a - b);
  const offsetsA: number[] = [];
  const offsetsB: number[] = [];

  for (let i = 0; i < zBreaks.length - 1; i++) {
    const zMid = (zBreaks[i] + zBreaks[i + 1]) * 0.5;
    offsetsA.push(findOffsetAtZ(profileA, zMid));
    offsetsB.push(findOffsetAtZ(profileB, zMid));
  }

  return { zBreaks, offsetsA, offsetsB };
}

/** Returns the offset from a profile at a given Z, or 0 if no band covers it. */
function findOffsetAtZ(profile: CornerOffsetProfile, z: number): number {
  for (const band of profile) {
    if (z >= band.zBottom && z <= band.zTop) {
      return band.offset;
    }
  }
  return 0;
}

/**
 * Generates mitered front face quads and horizontal step trapezoids
 * at a convex corner. Processes merged Z bands bottom-to-top, tracking
 * the previous band's state to emit step faces at offset transitions.
 */
function addCornerMiterFaces(
  polygons: PolygonBSP[],
  cornerX: number,
  cornerY: number,
  dirAx: number,
  dirAy: number,
  dirBx: number,
  dirBy: number,
  merged: MergedCornerBands,
  addBottom: boolean,
): void {
  const { zBreaks, offsetsA, offsetsB } = merged;
  if (zBreaks.length < 2) {
    return;
  }

  const lenA = Math.sqrt(dirAx * dirAx + dirAy * dirAy);
  const lenB = Math.sqrt(dirBx * dirBx + dirBy * dirBy);
  if (lenA < MULTIGABLE_BSP_EPSILON || lenB < MULTIGABLE_BSP_EPSILON) {
    return;
  }

  // Outward normals (right-perpendicular of edge direction)
  const nAx = dirAy / lenA;
  const nAy = -dirAx / lenA;
  const nBx = dirBy / lenB;
  const nBy = -dirBx / lenB;

  let prevOffsetA = 0;
  let prevOffsetB = 0;
  let prevMiterX = cornerX;
  let prevMiterY = cornerY;
  let prevCornerAx = cornerX;
  let prevCornerAy = cornerY;
  let prevCornerBx = cornerX;
  let prevCornerBy = cornerY;

  for (let i = 0; i < zBreaks.length - 1; i++) {
    const zBottom = zBreaks[i];
    const zTop = zBreaks[i + 1];
    const offsetA = offsetsA[i];
    const offsetB = offsetsB[i];

    // Corner point offset along each edge's normal
    const cornerAx = cornerX + nAx * offsetA;
    const cornerAy = cornerY + nAy * offsetA;
    const cornerBx = cornerX + nBx * offsetB;
    const cornerBy = cornerY + nBy * offsetB;

    // Intersect the two offset lines to find the miter point.
    const miterIntersection = intersectRay2Ray2(cornerAx, cornerAy, dirAx, dirAy, cornerBx, cornerBy, dirBx, dirBy);
    if (!miterIntersection) {
      continue;
    }
    const miterX = miterIntersection.x;
    const miterY = miterIntersection.y;

    // Side A front face quad (cornerA -> miter point, extruded zBottom->zTop)
    if (vec2DistSquared(cornerAx, cornerAy, miterX, miterY) >= MULTIGABLE_BSP_EPSILON_SQUARED) {
      const plane = planeBSPByThreePoints(cornerAx, cornerAy, zBottom, miterX, miterY, zBottom, miterX, miterY, zTop);
      if (plane) {
        const polygon = polygonBSPNew(
          0,
          [cornerAx, cornerAy, zBottom, miterX, miterY, zBottom, miterX, miterY, zTop, cornerAx, cornerAy, zTop],
          plane,
        );
        polygons.push(polygon);
      }
    }

    // Side B front face quad (miter point -> cornerB, extruded zBottom->zTop)
    if (vec2DistSquared(miterX, miterY, cornerBx, cornerBy) >= MULTIGABLE_BSP_EPSILON_SQUARED) {
      const plane = planeBSPByThreePoints(
        miterX,
        miterY,
        zBottom,
        cornerBx,
        cornerBy,
        zBottom,
        cornerBx,
        cornerBy,
        zTop,
      );
      if (plane) {
        const polygon = polygonBSPNew(
          0,
          [miterX, miterY, zBottom, cornerBx, cornerBy, zBottom, cornerBx, cornerBy, zTop, miterX, miterY, zTop],
          plane,
        );
        polygons.push(polygon);
      }
    }

    // Step trapezoids where offset changed from previous band
    if (addBottom || i > 0) {
      // const aIncreasing = offsetA - prevOffsetA > MULTIGABLE_BSP_EPSILON;
      // const bIncreasing = offsetB - prevOffsetB > MULTIGABLE_BSP_EPSILON;//TODO: actually just need to get intersection

      const intPrevACurrB = intersectLine2Line2(
        prevCornerAx,
        prevCornerAy,
        prevMiterX,
        prevMiterY,
        cornerBx,
        cornerBy,
        miterX,
        miterY,
        undefined,
        ZERO_EPSILON,
      );
      const intCurrAPrevB = intersectLine2Line2(
        cornerAx,
        cornerAy,
        miterX,
        miterY,
        prevCornerBx,
        prevCornerBy,
        prevMiterX,
        prevMiterY,
        undefined,
        ZERO_EPSILON,
      );

      if (Math.abs(offsetA - prevOffsetA) > MULTIGABLE_BSP_EPSILON) {
        let trapPrevMiterX = prevMiterX;
        let trapPrevMiterY = prevMiterY;
        let trapMiterX = miterX;
        let trapMiterY = miterY;

        // If the previous and current paths overlap, use the point they overlap at instead of the miter point
        if (intPrevACurrB !== null && intPrevACurrB instanceof CurveCurvePointIntersection) {
          trapPrevMiterX = intPrevACurrB.x;
          trapPrevMiterY = intPrevACurrB.y;
        } else if (intCurrAPrevB !== null && intCurrAPrevB instanceof CurveCurvePointIntersection) {
          trapMiterX = intCurrAPrevB.x;
          trapMiterY = intCurrAPrevB.y;
        }

        addCornerStepTrapezoid(
          polygons,
          zBottom,
          prevCornerAx,
          prevCornerAy,
          trapPrevMiterX,
          trapPrevMiterY,
          cornerAx,
          cornerAy,
          trapMiterX,
          trapMiterY,
          offsetA > prevOffsetA,
        );
      }
      if (Math.abs(offsetB - prevOffsetB) > MULTIGABLE_BSP_EPSILON) {
        let trapPrevMiterX = prevMiterX;
        let trapPrevMiterY = prevMiterY;
        let trapMiterX = miterX;
        let trapMiterY = miterY;

        // If the previous and current paths overlap, use the point they overlap at instead of the miter point
        if (intPrevACurrB !== null && intPrevACurrB instanceof CurveCurvePointIntersection) {
          trapMiterX = intPrevACurrB.x;
          trapMiterY = intPrevACurrB.y;
        } else if (intCurrAPrevB !== null && intCurrAPrevB instanceof CurveCurvePointIntersection) {
          trapPrevMiterX = intCurrAPrevB.x;
          trapPrevMiterY = intCurrAPrevB.y;
        }

        addCornerStepTrapezoid(
          polygons,
          zBottom,
          trapPrevMiterX,
          trapPrevMiterY,
          prevCornerBx,
          prevCornerBy,
          trapMiterX,
          trapMiterY,
          cornerBx,
          cornerBy,
          offsetB > prevOffsetB,
        );
      }
    }

    // Update previous-band state
    prevOffsetA = offsetA;
    prevOffsetB = offsetB;
    prevMiterX = miterX;
    prevMiterY = miterY;
    prevCornerAx = cornerAx;
    prevCornerAy = cornerAy;
    prevCornerBx = cornerBx;
    prevCornerBy = cornerBy;
  }

  // Final step trapezoids at the top: close off against offset 0
  if (zBreaks.length >= 2) {
    const zTop = zBreaks[zBreaks.length - 1];

    if (Math.abs(prevOffsetA) > MULTIGABLE_BSP_EPSILON) {
      addCornerStepTrapezoid(
        polygons,
        zTop,
        prevCornerAx,
        prevCornerAy,
        prevMiterX,
        prevMiterY,
        cornerX,
        cornerY,
        cornerX,
        cornerY,
        false,
      );
    }
    if (Math.abs(prevOffsetB) > MULTIGABLE_BSP_EPSILON) {
      addCornerStepTrapezoid(
        polygons,
        zTop,
        prevMiterX,
        prevMiterY,
        prevCornerBx,
        prevCornerBy,
        cornerX,
        cornerY,
        cornerX,
        cornerY,
        false,
      );
    }
  }
}

/**
 * Creates a horizontal trapezoid at a Z boundary between two bands
 * with different offsets. The four corners are the offset-corner and
 * miter points from the previous and current bands.
 *
 * When stepOut is true (offset increased), the normal points downward.
 * When stepOut is false (offset decreased), the normal points upward.
 */
function addCornerStepTrapezoid(
  polygons: PolygonBSP[],
  z: number,
  prevAx: number,
  prevAy: number,
  prevBx: number,
  prevBy: number,
  currAx: number,
  currAy: number,
  currBx: number,
  currBy: number,
  stepOut: boolean,
): void {
  const prevCollapsed = vec2DistSquared(prevAx, prevAy, prevBx, prevBy) < MULTIGABLE_BSP_EPSILON_SQUARED;
  const currCollapsed = vec2DistSquared(currAx, currAy, currBx, currBy) < MULTIGABLE_BSP_EPSILON_SQUARED;

  if (prevCollapsed && currCollapsed) {
    return;
  }

  let verts: number[];
  if (prevCollapsed) {
    if (stepOut) {
      verts = [prevAx, prevAy, z, currBx, currBy, z, currAx, currAy, z];
    } else {
      verts = [currAx, currAy, z, currBx, currBy, z, prevAx, prevAy, z];
    }
  } else if (currCollapsed) {
    if (stepOut) {
      verts = [currAx, currAy, z, prevBx, prevBy, z, prevAx, prevAy, z];
    } else {
      verts = [prevAx, prevAy, z, prevBx, prevBy, z, currAx, currAy, z];
    }
  } else if (stepOut) {
    verts = [currAx, currAy, z, prevAx, prevAy, z, prevBx, prevBy, z, currBx, currBy, z];
  } else {
    verts = [prevAx, prevAy, z, prevBx, prevBy, z, currBx, currBy, z, currAx, currAy, z];
  }

  const plane = planeBSPByThreePoints(
    verts[0],
    verts[1],
    verts[2],
    verts[3],
    verts[4],
    verts[5],
    verts[6],
    verts[7],
    verts[8],
  );
  if (!plane) {
    return;
  }

  const polygon = polygonBSPNew(0, verts, plane);
  polygons.push(polygon);
}

interface CornerOffsetBand {
  readonly zBottom: number;
  readonly zTop: number;
  readonly offset: number;
}

type CornerOffsetProfile = CornerOffsetBand[];

interface WallPlaneGridSlice {
  readonly grid: (WallPlaneSubdomainMiterMass | undefined)[][];
  readonly plane: Plane;
  readonly startRow: number;
  readonly endRow: number;
  readonly startCol: number;
  readonly endCol: number;
  readonly minU: number;
  readonly maxU: number;
  readonly minV: number;
  readonly maxV: number;
}

interface ParallelWallPlaneGridMatch {
  readonly hash: string;
  readonly grid: (WallPlaneSubdomainMiterMass | undefined)[][];
  readonly plane: Plane;
}

interface ResolvedEdgeGrid {
  startx: number;
  starty: number;
  endx: number;
  endy: number;
  gridSlice: WallPlaneGridSlice | undefined;
}

function getWallPlaneGridsFromRoofEdges(
  edges: RoofEdge[],
  wallPlaneMasses: WallPlaneMasses,
  bottomOffset: number,
  peakHeight: number,
): ResolvedEdgeGrid[] {
  const roofEdges: PolyLine2[] = [];
  const gridMatches: (ParallelWallPlaneGridMatch | undefined)[] = [];
  const resolvedEdges: ResolvedEdgeGrid[] = [];

  // Initial pass on roof edges to combine coplanar edges
  // find a non-coplanar starting point
  let startI = 0;
  for (; startI < edges.length; startI++) {
    const prevI = (startI + edges.length - 1) % edges.length;
    const edge = edges[startI];
    const edgePrev = edges[prevI];

    const getPlane = (edge: RoofEdge) => {
      const [sx, sy, ex, ey] = edge.edge;
      const plane = planeCanonicalFromSegment(sx, sy, ex, ey, LEVEL_XY_PLANE);
      if (plane === null) {
        throw new Error("Malformed Roof Edge");
      }
      return plane;
    };

    const plane = getPlane(edge);
    const planePrev = getPlane(edgePrev);

    if (planeIsCoplanar(plane, planePrev) === false) {
      break;
    }
  }

  if (startI >= edges.length) {
    throw new Error("Malformed Roof Edges");
  }

  let startx = edges[startI].edge[0];
  let starty = edges[startI].edge[1];
  let i = startI;
  do {
    const nextI = (i + 1) % edges.length;

    const edge = edges[i];
    const edgeNext = edges[nextI];

    const getPlane = (edge: RoofEdge) => {
      const [sx, sy, ex, ey] = edge.edge;
      const plane = planeCanonicalFromSegment(sx, sy, ex, ey, LEVEL_XY_PLANE);
      if (plane === null) {
        throw new Error("Malformed Roof Edge");
      }
      return plane;
    };

    const plane = getPlane(edge);
    const planeNext = getPlane(edgeNext);

    if (planeIsCoplanar(plane, planeNext)) {
      i = nextI;
      continue;
    }

    roofEdges.push(polyLine2New([startx, starty, edgeNext.edge[0], edgeNext.edge[1]]));

    startx = edgeNext.edge[0];
    starty = edgeNext.edge[1];

    i = nextI;
  } while (i !== startI);

  // Find a valid wall plane (if it exists) for each edge
  for (const edge of roofEdges) {
    gridMatches.push(getWallPlaneGridMatchFromEdge(edge, wallPlaneMasses));
    const [startx, starty, endx, endy] = edge;
    resolvedEdges.push({
      startx,
      starty,
      endx,
      endy,
      gridSlice: undefined,
    });
  }

  // Resolve corners from adjacent matched wall planes. Exact-outline edges are
  // already on the wall core, so this keeps endpoints on those planes.
  for (let i = 0; i < gridMatches.length; i++) {
    const nextI = (i + 1) % gridMatches.length;
    const match = gridMatches[i];
    const matchNext = gridMatches[nextI];

    if (match === undefined && matchNext === undefined) {
      continue;
    }

    const edge = roofEdges[i];
    const edgeNext = roofEdges[nextI];

    const getPlane = (edge: PolyLine2, match: ParallelWallPlaneGridMatch | undefined) => {
      if (match !== undefined) {
        return match.plane;
      } else {
        const [sx, sy, ex, ey] = edge;
        const planeCanonical = planeCanonicalFromSegment(sx, sy, ex, ey, LEVEL_XY_PLANE);
        if (planeCanonical === null) {
          throw new Error("Malformed Roof Edges");
        }
        return planeCanonical;
      }
    };
    const plane = getPlane(edge, match);
    const planeNext = getPlane(edgeNext, matchNext);

    let intersectx: number, intersecty: number;
    if (planeIsCoplanar(plane, planeNext)) {
      intersectx = edgeNext[0];
      intersecty = edgeNext[1];
    } else {
      const intersect = intersectPlanePlane(plane, planeNext);
      if (intersect === null) {
        throw new Error("Malformed Roof Edges");
      }
      intersectx = intersect.originX;
      intersecty = intersect.originY;
    }

    const resolved = resolvedEdges[i];
    const resolvedNext = resolvedEdges[nextI];

    //Project the intersection point back into each plane. For the non-coplanar case this *would* be unnecessary
    //if it weren't for floating point imprecision, but downstream logic requires these points to be exactly in the plane.
    [resolved.endx, resolved.endy] = planeProject3(plane, intersectx, intersecty, 0);
    [resolvedNext.startx, resolvedNext.starty] = planeProject3(planeNext, intersectx, intersecty, 0);
  }

  //Get the grid slice metadata for each resolved edge
  for (let i = 0; i < resolvedEdges.length; i++) {
    const resolved = resolvedEdges[i];
    const match = gridMatches[i];

    if (!match) {
      continue;
    }

    resolved.gridSlice = calculateEdgeGridMeta(
      resolved.startx,
      resolved.starty,
      resolved.endx,
      resolved.endy,
      bottomOffset,
      peakHeight,
      match.plane,
      match.grid,
    );
  }

  return resolvedEdges;
}

/**
 * Maps a roof perimeter edge to its corresponding wall-plane mass grid
 * region. Returns undefined when the edge does not correspond to any
 * wall plane (e.g. a gable-end edge with no wall behind it).
 */
function getWallPlaneGridMatchFromEdge(
  edge: PolyLine2,
  wallPlaneMasses: WallPlaneMasses,
): ParallelWallPlaneGridMatch | undefined {
  const [sx, sy, ex, ey] = edge;

  if (!wallPlaneMasses.massGrids) {
    return undefined;
  }

  const edgePlane = planeCanonicalFromSegment(sx, sy, ex, ey, LEVEL_XY_PLANE);
  if (!edgePlane) {
    return undefined;
  }

  const hash = planeHash(edgePlane);
  const grid = wallPlaneMasses.massGrids[hash]?.grid;
  if (!grid) {
    return undefined;
  }

  return {
    hash,
    grid,
    plane: edgePlane,
  };
}

/**
 * Defines the subset of a wall plane grid enclosed by the start, end, bottomOffset, and peakHeight of a roof edge
 */
function calculateEdgeGridMeta(
  sx: number,
  sy: number,
  ex: number,
  ey: number,
  bottomOffset: number,
  peakHeight: number,
  wallPlane: Plane,
  grid: (WallPlaneSubdomainMiterMass | undefined)[][],
): WallPlaneGridSlice | undefined {
  // Project the roof edge endpoints into the wall plane's UV space.
  const [startU] = planeProject(wallPlane, sx, sy, 0);
  const [endU] = planeProject(wallPlane, ex, ey, 0);
  const minU = Math.min(startU, endU);
  const maxU = Math.max(startU, endU);

  // Vertical extent of the clipping volume in world Z
  const minV = bottomOffset;
  const maxV = peakHeight;

  // Find the row and column bounds that overlap the requested region.
  // Only parallel miter edges count — cross-wall cells must not contribute
  // side planes for this roof edge.
  let startRow = -1;
  let endRow = -1;
  let startCol = Number.MAX_SAFE_INTEGER;
  let endCol = -1;
  for (let row = 0; row < grid.length; row++) {
    const rowCells = grid[row];
    if (!rowCells) {
      continue;
    }

    // Check any cell in this row for the datum band Z range. All cells
    // in the same row share the same bottom/top.
    let rowBottom: number | undefined;
    let rowTop: number | undefined;
    for (const cell of rowCells) {
      if (cell) {
        rowBottom = cell.bottom;
        rowTop = cell.top;
        break;
      }
    }

    if (rowBottom === undefined || rowTop === undefined) {
      continue;
    }

    // Skip rows that don't overlap the vertical range
    if (rowTop <= minV || rowBottom >= maxV) {
      continue;
    }

    let rowHasQualifyingCell = false;

    // Scan columns for U overlap among parallel miter edges only
    for (let col = 0; col < rowCells.length; col++) {
      const cell = rowCells[col];
      if (!cell) {
        continue;
      }

      const subdomain = cell.miterEdge.subdomain;
      const cellMinU = subdomain.minX;
      const cellMaxU = subdomain.maxX;

      if (cellMaxU <= minU || cellMinU >= maxU) {
        continue;
      }

      rowHasQualifyingCell = true;
      startCol = Math.min(startCol, col);
      endCol = Math.max(endCol, col);
    }

    if (!rowHasQualifyingCell) {
      continue;
    }

    if (startRow === -1) {
      startRow = row;
    }
    endRow = row;
  }

  if (startRow === -1 || endCol === -1) {
    return undefined;
  }

  return {
    grid,
    plane: wallPlane,
    startRow,
    endRow,
    startCol,
    endCol,
    minU,
    maxU,
    minV,
    maxV,
  };
}
